// @react
import React, { ReactElement, useEffect, useState } from 'react'
// @types
import {
  OrderInvoiceUrlQuery,
  OrderInvoiceUrlQueryVariables,
} from 'lib/graphql/graphqlTypes'
import { PropsType as ItemProps } from './LineItem'
// @graphQL
import { useQuery } from '@apollo/client'
import { GET_ORDER_URL } from 'graphql/queries/order.query'
// @libraries
import { makeStyles, Theme } from '@material-ui/core/styles'
// @components
import Grid from '@bit/atd.web.grid'
import Button from '@bit/atd.web.button'
import colors from '@bit/atd.web.colors'
// @card parts
import Wrap from '../../Parts/Wrap'
import OrderItem from './LineItem'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '24px',
  },
  headerContainer: {
    flexDirection: 'column',
    position: 'relative',
    paddingBottom: '24px',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '-24px',
      right: '-24px',
      bottom: 0,
      borderBottom: `1px solid ${colors.gray300}`,
    },
  },
  headerColumn: {
    borderBottom: `1px solid ${colors.gray300}`,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'baseline',
    paddingBottom: '8px',
    marginBottom: '8px',
    [theme.breakpoints.up('sm')]: {
      borderBottom: `none`,
      flexDirection: 'column',
      marginRight: '40px',
      width: 'auto',
      paddingBottom: '0',
      marginBottom: '0',
    },
  },
  btnWrap: {
    marginTop: '16px',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: 'auto',
      width: 'auto',
    },
    '& .MuiButtonBase-root': {
      textAlign: 'center',
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: 'auto',
      },
    },
  },
  label: {
    display: 'block',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '12px',
    textTransform: 'uppercase',
    color: '#7C7C7C',
    alignItems: 'baseline',
    [theme.breakpoints.up('sm')]: {
      marginBottom: '8px',
    },
  },
  value: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '20px',
    color: colors.textActive,
    flexGrow: 1,
    textAlign: 'right',
    [theme.breakpoints.up('sm')]: {
      flexGrow: 0,
      textAlign: 'left',
    },
  },
}))

export type PropsType = {
  orderNumber: string
  date?: string
  total?: number
  shippedTo?: string
  receiptUrl?: string
  onAutoRenewChange?: (
    autoRenew: ItemProps['autoRenew'],
    orderNumber: PropsType['orderNumber'],
    orderLineNumber: ItemProps['orderLineNumber'],
    title: ItemProps['title'],
  ) => void
  lineItems: {
    url?: ItemProps['url']
    downloadURL?: ItemProps['downloadURL']
    title: ItemProps['title']
    imageURL?: ItemProps['imageURL']
    titleURL?: ItemProps['titleURL']
    description?: ItemProps['description']
    category?: ItemProps['category']
    price?: ItemProps['price']
    type?: ItemProps['type']
    autoRenew?: ItemProps['autoRenew']
    orderLineNumber?: ItemProps['orderLineNumber']
  }[]
}

const CardOrders = ({
  orderNumber,
  date,
  total,
  shippedTo,
  lineItems,
  onAutoRenewChange,
}: PropsType): ReactElement => {
  /****************************************
   *  STATE
   ****************************************/

  const [isViewReceipt, setIsViewReceipt] = useState(0)

  /****************************************
   *  HOOKS
   ****************************************/

  const classes = useStyles()
  const { data } = useQuery<
    OrderInvoiceUrlQuery,
    OrderInvoiceUrlQueryVariables
  >(GET_ORDER_URL, {
    skip: isViewReceipt === 0,
    variables: {
      input: {
        orderNumber: orderNumber,
      },
    },
  })

  useEffect(() => {
    if (data?.orderInvoiceUrl.url && isViewReceipt > 0) {
      openInNewTab(data.orderInvoiceUrl.url)
    }
  }, [data, isViewReceipt])

  /****************************************
   *  FUNCTIONS
   ****************************************/

  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, '_blank')
    if (newWindow) newWindow.opener = null
  }

  /****************************************
   *  RENDER
   ****************************************/

  return (
    <Wrap className={classes.root}>
      {/* Header */}
      <Grid container alignItems="center" className={classes.headerContainer}>
        <Grid item className={classes.headerColumn}>
          <span className={classes.label}>Order placed</span>
          <span className={classes.value}>{date}</span>
        </Grid>
        <Grid item className={classes.headerColumn}>
          <span className={classes.label}>Total</span>
          <span className={classes.value}>${total?.toFixed(2)}</span>
        </Grid>
        <Grid item className={classes.headerColumn}>
          <span className={classes.label}>Shipped to</span>
          <span className={classes.value}>{shippedTo}</span>
        </Grid>
        <Grid item className={classes.headerColumn}>
          <span className={classes.label}>Number #</span>
          <span className={classes.value}>{orderNumber}</span>
        </Grid>
        <Grid item className={classes.btnWrap}>
          <Button
            onClick={() => setIsViewReceipt((prevState) => prevState + 1)}
            variant="secondary"
            size="small"
          >
            View Receipt
          </Button>
        </Grid>
      </Grid>

      {/* Order Items  */}
      {lineItems &&
        lineItems?.map((item, index) => {
          return (
            <OrderItem
              key={index}
              url={item?.url}
              downloadURL={item?.downloadURL}
              imageURL={item.imageURL}
              title={item.title}
              titleURL={item.titleURL}
              description={item.description}
              category={item.category}
              price={item?.price}
              type={item?.type}
              autoRenew={item?.autoRenew}
              onAutoRenewChange={() =>
                onAutoRenewChange &&
                onAutoRenewChange(
                  item.autoRenew,
                  orderNumber,
                  item.orderLineNumber,
                  item.title,
                )
              }
            />
          )
        })}
    </Wrap>
  )
}

export default CardOrders
